import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { FC, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";

import {
  DesignToken,
  NavigationTopBar,
  NavigationTopBarAlert,
  RadiusToken,
  SpacingToken,
} from "@livechat/design-system-react-components";

import logoContext from "assets/svg/logo-context-2025.svg";
import { Context2025PromoLinkButton } from "components/Context2025PromoLinkButton";
import Flex from "components/layout/Flex";
import { SubscriptionPhase } from "constants/subscription-phase";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { campaigns, useLimitedTimeCampaign } from "hooks/use-limited-time-campaigns";
import { isPushNotificationSupported } from "routes/agents/agent-form/browser-notifications/utils";
import { AgentFormSectionId } from "routes/agents/agent-form/constants";
import { AgentsRoutes } from "routes/agents/constants";
import { EmailSettingsRoutes } from "routes/settings/inboxes/routes";
import { SubscriptionRoutes } from "routes/subscription";
import {
  AgentActions,
  selectHasSeenEnableNotificationBanner,
  selectIsAdmin,
  selectIsViewer,
} from "store/entities/agents";
import { selectIsPastDue, selectMaxWebpushSubscriptionsPerAgent } from "store/entities/license";
import { getCurrentSubscriptionDaysLeft, selectSubscriptionPhase } from "store/entities/subscriptions";
import { selectCanAccessSettings } from "store/features/app";
import { getCurrentAgentId } from "store/features/session";
import { selectTicketsForwardedToHelpDesk } from "store/views/tickets/selectors";
import { withVar } from "themes/helpers";
import { plural } from "utils/string";

import spinImage from "./assets/light-spin-symbol.svg";

const CONTEXT_2025_PROMO_LANDING_PAGE_URL =
  "http://www.text.com/context25?utm_source=app.helpdesk.com&utm_medium=inappbanner&utm_campaign=context25&utm_content=button";

interface IProps {
  isMobile: boolean;
}

const NotificationsBar: FC<IProps> = ({ isMobile }) => {
  const isViewer = useSelector(selectIsViewer);
  const isAdmin = useSelector(selectIsAdmin);
  const subscriptionPhase = useSelector(selectSubscriptionPhase);
  const ticketsForwardedToHelpDesk = useSelector(selectTicketsForwardedToHelpDesk);
  const hasSeenEnableNotificationBanner = useSelector(selectHasSeenEnableNotificationBanner);
  const maxWebpushSubscriptionsPerAgent = useSelector(selectMaxWebpushSubscriptionsPerAgent);
  const isPastDue = useSelector(selectIsPastDue);
  const { isCampaignActive: isContext2025PromoCampaignActive } = useLimitedTimeCampaign(
    campaigns.context2025PromoTopBarAlert
  );
  const [closedContext2025PromoTopBarAlert, setClosedContext2025PromoTopBarAlert] = useState(false);

  const showPastDueAlert = isPastDue && isAdmin;
  const showForwardingDisabledBanner = !ticketsForwardedToHelpDesk;
  const showTrialDaysLeftAlert = isAdmin && subscriptionPhase === SubscriptionPhase.TrialNoCC;
  const showEnableNotificationBanner =
    !hasSeenEnableNotificationBanner &&
    !isViewer &&
    isPushNotificationSupported() &&
    maxWebpushSubscriptionsPerAgent > 0;
  const showViewerRoleUpgradeAlert = isViewer;

  const handleContext2025PromoClose = (): void => {
    setClosedContext2025PromoTopBarAlert(true);
  };

  // We need to display only one notification at a time but render all of them to track their visibility, so every next notification is based on the previous one
  const context2025PromoVisible = isContext2025PromoCampaignActive && !closedContext2025PromoTopBarAlert;

  const pastDueVisible = !context2025PromoVisible && showPastDueAlert;

  const forwardingDisabledVisible = !context2025PromoVisible && !pastDueVisible && showForwardingDisabledBanner;

  const enableNotificationVisible =
    !context2025PromoVisible && !pastDueVisible && !forwardingDisabledVisible && showEnableNotificationBanner;

  const trialDaysLeftVisible =
    !context2025PromoVisible &&
    !pastDueVisible &&
    !forwardingDisabledVisible &&
    !enableNotificationVisible &&
    showTrialDaysLeftAlert;

  const viewerRoleUpgradeVisible =
    !context2025PromoVisible &&
    !pastDueVisible &&
    !forwardingDisabledVisible &&
    !enableNotificationVisible &&
    !trialDaysLeftVisible &&
    showViewerRoleUpgradeAlert;

  const hasAnyAlertVisible =
    context2025PromoVisible ||
    pastDueVisible ||
    forwardingDisabledVisible ||
    enableNotificationVisible ||
    trialDaysLeftVisible ||
    viewerRoleUpgradeVisible;

  return (
    <NavigationTopBarWrapper isMobile={isMobile} hasAnyAlertVisible={hasAnyAlertVisible}>
      <NavigationTopBar>
        <Context2025PromoAlert isVisible={context2025PromoVisible} onClose={handleContext2025PromoClose} />
        <PastDueAlert isVisible={pastDueVisible} />
        <ForwardingDisabledAlert isVisible={forwardingDisabledVisible} />
        <EnableNotificationsAlert isVisible={enableNotificationVisible} />
        <TrialDaysLeftAlert isVisible={trialDaysLeftVisible} />
        <ViewerRoleUpgradeAlert isVisible={viewerRoleUpgradeVisible} />
      </NavigationTopBar>
    </NavigationTopBarWrapper>
  );
};

interface IAlertProps {
  isVisible: boolean;
  onClose?: () => void;
}

const Context2025PromoAlert: FC<IAlertProps> = ({ isVisible, onClose }) => {
  const handleClose = (): void => {
    onClose?.();
  };

  return (
    <Context2025TopBarAlert
      isVisible={isVisible}
      closeButton={{
        onClick: handleClose,
      }}
    >
      <Flex
        gap={withVar(SpacingToken.Spacing2)}
        alignItems="center"
        css={css`
          font-family: Lora, sans-serif;
          font-size: 14px;
          font-weight: 500;
          line-height: 140%;
        `}
      >
        <img src={spinImage} alt="Spin image" />
        Don’t miss{" "}
        <img
          css={css`
            height: 12px;
          `}
          src={logoContext}
          alt="Context 2025"
        />{" "}
        — where you say goodbye to yesterday’s support.
        <Context2025PromoLinkButton href={CONTEXT_2025_PROMO_LANDING_PAGE_URL} onClick={handleClose} size="compact" />
      </Flex>
    </Context2025TopBarAlert>
  );
};

const ForwardingDisabledAlert: FC<IAlertProps> = ({ isVisible }) => {
  const history = useHistory();
  const canAccessSettings = useSelector(selectCanAccessSettings);

  const handleFollowInstructionsClick = (): void => {
    trackEngagement("Notifications bar: Follow forwarding instructions link clicked", EventPlace.Onboarding);
    history.push(EmailSettingsRoutes.ForwardingAddressesWithInstructions);
  };

  return (
    <TopBarAlert
      kind="warning"
      isVisible={isVisible}
      primaryCta={
        canAccessSettings
          ? {
              label: "Read setup instructions",
              onClick: handleFollowInstructionsClick,
            }
          : undefined
      }
    >
      <b>{`Set up email forwarding to get tickets.${canAccessSettings ? "" : " Ask your Admins to complete the setup."}`}</b>
    </TopBarAlert>
  );
};

const EnableNotificationsAlert: FC<IAlertProps> = ({ isVisible }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const currentAgentId = useSelector(getCurrentAgentId);

  const handleClose = (): void => {
    dispatch(
      AgentActions.setFlag({ name: "hasSeenEnableNotificationBanner_bba3563bc528023d6d1353cf26b0c796", value: true })
    );
    trackEngagement("Browser notifications: Banner closed", EventPlace.Agents);
  };

  const handleRedirect = (): void => {
    history.push(`${AgentsRoutes.Agents}/${currentAgentId}/edit/#${AgentFormSectionId.BrowserNotifications}`);
    trackEngagement("Browser notifications: Redirect to browser notifications section", EventPlace.Agents);
  };

  return (
    <TopBarAlert
      kind="info"
      isVisible={isVisible}
      primaryCta={{
        label: "Enable notifications",
        onClick: handleRedirect,
      }}
      closeButton={{
        onClick: handleClose,
      }}
    >
      <b>Enable browser notifications to be updated.</b>
    </TopBarAlert>
  );
};

const TrialDaysLeftAlert: FC<IAlertProps> = ({ isVisible }) => {
  const history = useHistory();
  const daysLeft = useSelector(getCurrentSubscriptionDaysLeft);

  const handleSubscribe = (): void => {
    history.push(SubscriptionRoutes.Plans);
    trackEngagement("Notifications bar: Trial counter subscribe link click", EventPlace.Onboarding);
  };

  const splitDays = daysLeft.toString().split("");

  return (
    <TopBarAlert
      kind="info"
      isVisible={isVisible}
      primaryCta={{
        label: "Subscribe now",
        onClick: handleSubscribe,
      }}
    >
      <Flex alignItems="center">
        {splitDays.map((digit, index) => (
          <DigitCounter key={index}>{digit}</DigitCounter>
        ))}
        <b>{plural(daysLeft, "day", "days")} left in your trial.</b>
      </Flex>
    </TopBarAlert>
  );
};

const ViewerRoleUpgradeAlert: FC<IAlertProps> = ({ isVisible }) => {
  const history = useHistory();

  const handleContactAdmin = (): void => {
    history.push(AgentsRoutes.Agents);
  };

  return (
    <TopBarAlert
      kind="warning"
      isVisible={isVisible}
      primaryCta={{
        label: "Contact admin to upgrade",
        onClick: handleContactAdmin,
      }}
    >
      <b>You have a viewer role.</b>
    </TopBarAlert>
  );
};

const PastDueAlert: FC<IAlertProps> = ({ isVisible }) => {
  const history = useHistory();

  const handleRedirectToBilling = (): void => {
    history.push(SubscriptionRoutes.AccountDetailsWithOpenedBilling);
    trackEngagement("Notifications bar: Update billing details link clicked", EventPlace.Onboarding);
  };

  return (
    <TopBarAlert
      kind="error"
      isVisible={isVisible}
      primaryCta={{
        label: "Update billing details",
        onClick: handleRedirectToBilling,
      }}
    >
      <b>Subscription payment failed.</b>
    </TopBarAlert>
  );
};

const TopBarAlert = styled(NavigationTopBarAlert)`
  border-radius: var(${RadiusToken.Radius3});
`;

const Context2025TopBarAlert = styled(TopBarAlert)`
  background-color: #0f1d18;
  color: #f8ffed;
`;

const NavigationTopBarWrapper = styled.div<{ isMobile: boolean; hasAnyAlertVisible: boolean }>`
  padding-top: ${({ hasAnyAlertVisible }): string => (hasAnyAlertVisible ? "6px" : "0")};
  padding-left: ${({ isMobile }): string => (isMobile ? "6px" : "0")};
  padding-right: 6px;
  padding-bottom: 0;
`;

const DigitCounter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  height: var(${SpacingToken.Spacing6});
  width: var(${SpacingToken.Spacing5});
  color: var(${DesignToken.ContentLockedDefault});
  background-color: var(${DesignToken.SurfaceLockedDefault});
  border-radius: var(${RadiusToken.Radius2});
  margin-right: var(${SpacingToken.Spacing1});
`;

export { NotificationsBar };
